import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
/* -----------------Globals--------------- */
import { useCallback, useRef, useState } from "react";

/* -----------------Types--------------- */
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";

/* -----------------Components--------------- */
import Box from "@memorang/ui/src/components/Box";
import SessionList from "./SessionList";

import { useSyncActiveScenarioFromItem } from "@features/proethics-simulation";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type { FlatList } from "react-native";
import { PassageStemModalContainer } from "../containers/PassageStemModalContainer";
import StudySessionBottombarContainer from "../containers/StudySessionBottombarContainer";
/* -----------------Containers--------------- */
import StudySessionFeedbackContainer from "../containers/StudySessionFeedbackContainer";
import useSessionStore from "../hooks/useSessionStore";
import { StudySessionAppBarContainer } from "./StudySessionAppBarContainer";

type Props = {
	sessionItems: BaseItemSchema[];
};

// Keeps the active ProEthics scenario in lockstep with the current item, so
// navigating back to an earlier scenario restores its chat/report (a no-op for
// items without a scenarioId). Isolated as a null component so subscribing to
// currentItemIndex doesn't re-render the whole session view on every Next/Prev.
const ActiveScenarioSync = ({
	sessionItems,
}: { sessionItems: BaseItemSchema[] }) => {
	const currentItemIndex = useSessionStore((s) => s.currentItemIndex);
	useSyncActiveScenarioFromItem(
		sessionItems[currentItemIndex]?.metadata?.scenarioId,
	);
	return null;
};

const StudySessionView = ({ sessionItems }: Props) => {
	const ref = useRef<FlatList<BaseItemSchema> | null>(null);
	const passageStemModalRef = useRef<{ scrollToHighlightedText: () => void }>(
		null,
	);

	const { isMobile } = useDevice();

	const isProethicaTest = useSessionStore(
		(state) => state.proethicaTestKind != null,
	);

	const [showReadMore, setShowReadMore] = useState(false);

	const toggleShowReadMore = useCallback(
		({ scrollToHighlight }: { scrollToHighlight?: boolean } = {}) => {
			if (scrollToHighlight) {
				passageStemModalRef.current?.scrollToHighlightedText();
			}
			setShowReadMore((prev) => !prev);
		},
		[],
	);

	const theme = useAppTheme();

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<ActiveScenarioSync sessionItems={sessionItems} />
			<BottomSheetModalProvider>
				<Box flex={1} paddingHorizontal={isMobile || isProethicaTest ? 0 : 16}>
					<StudySessionAppBarContainer />
					<Box flex={1} backgroundColor={theme.colors.surface}>
						<SessionList
							ref={ref}
							items={sessionItems}
							toggleShowReadMore={toggleShowReadMore}
						/>
						{!isWeb && <StudySessionFeedbackContainer />}
					</Box>
				</Box>
			</BottomSheetModalProvider>
			<StudySessionBottombarContainer
				// biome-ignore lint/suspicious/noExplicitAny: flashlist ref typing override
				flashlistRef={ref as any}
			/>
			<PassageStemModalContainer
				// biome-ignore lint/suspicious/noExplicitAny: passageStemModalRef typing override
				passageStemModalRef={passageStemModalRef as any}
				showReadMore={showReadMore}
				propToggleShowReadMore={() => setShowReadMore(false)}
			/>
		</Box>
	);
};

export default StudySessionView;
